import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Users, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientMicrosite = () => {
  const { slug } = useParams();
  const [microsite, setMicrosite] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const msResponse = await axios.get(`${API}/microsites/${slug}`);
        setMicrosite(msResponse.data);
        
        if (msResponse.data.packages.length > 0) {
          const pkgResponse = await axios.get(`${API}/packages`);
          const filteredPackages = pkgResponse.data.filter(pkg => 
            msResponse.data.packages.includes(pkg.id)
          );
          setPackages(filteredPackages);
        }
      } catch (error) {
        console.error('Error fetching microsite:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-maroon text-xl">Loading...</p>
      </div>
    );
  }

  if (!microsite) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-maroon text-xl mb-4">Client page not found</p>
          <Link to="/" className="btn-primary">Go to Homepage</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16" data-testid="client-microsite-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          {microsite.logo_url && (
            <img 
              src={microsite.logo_url} 
              alt={microsite.client_name}
              className="h-24 mx-auto mb-6"
            />
          )}
          <h1 className="text-5xl sm:text-6xl font-heading font-bold text-maroon mb-6" data-testid="microsite-title">
            {microsite.client_name}
          </h1>
          <p className="text-xl text-maroon/70 max-w-3xl mx-auto">{microsite.description}</p>
        </div>

        {packages.length === 0 ? (
          <div className="text-center py-12 card max-w-2xl mx-auto">
            <p className="text-maroon/70 mb-6">
              Exclusive travel packages for {microsite.client_name} members are being curated. 
              Contact us for personalized travel planning.
            </p>
            <Link to="/consultation" className="btn-primary inline-block">
              Request Custom Package
            </Link>
          </div>
        ) : (
          <div>
            <h2 className="text-3xl font-heading font-bold text-maroon mb-8 text-center">
              Exclusive Packages for {microsite.client_name}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {packages.map((pkg, idx) => (
                <Link 
                  key={pkg.id} 
                  to={`/packages/${pkg.id}`}
                  className="card group"
                  data-testid={`microsite-package-${idx}`}
                >
                  <div className="aspect-[4/3] mb-4 overflow-hidden rounded-sm border border-maroon/10">
                    <img 
                      src={pkg.image_url} 
                      alt={pkg.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <h3 className="font-heading font-bold text-2xl text-maroon mb-2">{pkg.title}</h3>
                  <p className="text-maroon/70 mb-4 line-clamp-2">{pkg.description}</p>
                  <div className="flex items-center justify-between text-sm text-maroon/60 mb-4">
                    <span className="flex items-center space-x-1">
                      <Calendar size={16} />
                      <span>{pkg.duration}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Users size={16} />
                      <span>{pkg.available_spots} spots</span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-heading font-bold text-maroon">${pkg.price}</span>
                    <span className="text-terracotta font-semibold flex items-center space-x-1">
                      <span>View Details</span>
                      <ArrowRight size={16} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-16 text-center card bg-maroon text-cream">
          <h3 className="text-2xl font-heading font-bold mb-4">Need a Custom Experience?</h3>
          <p className="mb-6 text-cream/90">
            Our team is ready to create a personalized travel package tailored specifically for 
            {microsite.client_name} members.
          </p>
          <Link to="/consultation" className="btn-secondary inline-block bg-transparent border-cream text-cream hover:bg-cream hover:text-maroon">
            Schedule Consultation
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ClientMicrosite;